import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Box, Typography } from '@mui/material';
import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox/Checkbox';
import {
  PolicyGetResponse,
  PolicyTransactionPatchRequest,
  PolicyTransactionSearchRequestQuery,
  PolicyTransactionSearchResponse,
} from '@lio/api-types';
import { TrashAlt } from '@assets/svgs';
import { ToastType } from '@components/toast/ToastType.enum';
import { UpdateBillPlanDialog, CreatePolicyTransactionDialog } from '@pages/PolicyDetails/dialogs';
import { IconButton } from '@components/iconButton';
import { ConfirmationDialog } from '@components/dialog/ConfirmationDialog';
import { useStore } from '@store/GlobalStore';
import {
  AllPolicyDialogTypes,
  DialogType,
  EndorsementPolicyDialogType,
  PolicyDialogType,
  PolicyTransaction,
  PolicyTransactionStatus,
  PolicyTransactionTypes,
  RenewDialogType,
  TransactionType,
} from '@models/policyTransaction';
import { TransactionsFieldNames } from '@pages/PolicyDetails/PolicyDetails.constants';
import {
  useDisassociateConditionalRenewalQuoteId,
  useGetPolicy,
  useSearchPolicyTransactions,
  useUpdatePolicyTransaction,
} from '@services/policy';
import { formatDateToTimezone } from '@utils/date';
import { getApiErrorMessage } from '@utils/getApiErrorMessage';
import { OverflowTooltip } from '@components/tooltip/OverflowTooltip';
import { GridLink } from '@components/gridLink/GridLink';
import { shallow } from 'zustand/shallow';
import { useSearchPolicyTerms } from '@services/policyTerm/useSearchPolicyTerms';
import { Table } from '@components/table/Table';
import * as styles from '@components/dataGrid/DataGrid.styles';
import { TableGridBaseColDef, TableGridRenderCellParams } from '@components/table/types/TableColumnDef.type';
import { BrokerReassignedDialog } from '@pages/PolicyDetails/dialogs/BrokerReassignedDialog';
import { formatCurrency } from '@utils/number';

const transactionTypeDialog = {
  [PolicyTransactionTypes.Endorsement]: EndorsementPolicyDialogType.Endorsement,
  [PolicyTransactionTypes.BillingPlanEndorsement]: EndorsementPolicyDialogType.UpdateBillPlan,
  [PolicyTransactionTypes.Reinstatement]: PolicyDialogType.Reinstatement,
  [PolicyTransactionTypes.Cancelation]: PolicyDialogType.Cancelation,
  [PolicyTransactionTypes.Rewrite]: PolicyDialogType.Rewrite,
  [PolicyTransactionTypes.Renewal]: PolicyDialogType.Renew,
  [PolicyTransactionTypes.ConditionalRenewal]: RenewDialogType.ConditionalRenewalView,
  [PolicyTransactionTypes.DoNotRenew]: RenewDialogType.DoNotRenewView,
  [PolicyTransactionTypes.ManageRoles]: PolicyDialogType.BrokerReassignment,
};

const getPolicyDetailsTransactionHistoryColumns = ({
  handleOpenDialog,
  isInternalUser,
}: {
  handleOpenDialog: (type: AllPolicyDialogTypes, transaction?: PolicyTransaction) => void;
  isInternalUser: boolean;
}): TableGridBaseColDef[] => {
  return [
    {
      field: TransactionsFieldNames.Type,
      headerName: 'Type',
      flex: 1.2,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => {
        const { transactionType } = params.row;

        if (isInternalUser) {
          if (Object.keys(transactionTypeDialog).includes(transactionType)) {
            return (
              <Button
                variant='text'
                sx={{ padding: '0' }}
                onClick={() => handleOpenDialog(transactionTypeDialog[transactionType], params.row)}
              >
                <OverflowTooltip>{TransactionType[transactionType]}</OverflowTooltip>
              </Button>
            );
          }
          if (transactionType === PolicyTransactionTypes.NewBusiness) {
            return (
              <OverflowTooltip tooltipText={TransactionType[transactionType]}>
                <GridLink to={`/quote/${params.row.quote?.id}`}>{TransactionType[transactionType]}</GridLink>
              </OverflowTooltip>
            );
          }
        }
        return <OverflowTooltip>{TransactionType[transactionType]}</OverflowTooltip>;
      },
    },
    {
      field: TransactionsFieldNames.EffectiveDate,
      headerName: 'Effective Date',
      flex: 1,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => (
        <OverflowTooltip>{formatDateToTimezone(params.row.effectiveOn)}</OverflowTooltip>
      ),
    },
    {
      field: TransactionsFieldNames.ProcessedDate,
      headerName: 'Processed Date',
      flex: 1,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => {
        const { processedOn, originalProcessedOn } = params.row;
        const displayDate = originalProcessedOn ?? processedOn;
        const displayText = displayDate ? formatDateToTimezone(displayDate) : '-';
        const tooltipText =
          originalProcessedOn && processedOn ? `Reprocessed date: ${formatDateToTimezone(processedOn)}` : undefined;

        return (
          <OverflowTooltip tooltipText={tooltipText} forceShow={!!tooltipText}>
            {displayText}
          </OverflowTooltip>
        );
      },
    },
    {
      field: TransactionsFieldNames.Change,
      headerName: 'Change',
      flex: 1,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => {
        const { change } = params.row;
        return <OverflowTooltip>{change !== null ? formatCurrency(change) : '-'}</OverflowTooltip>;
      },
    },
    {
      field: TransactionsFieldNames.PolicyCostAfter,
      headerName: 'Policy Cost After',
      flex: 1,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => {
        const { policyCostAfter } = params.row;
        return <OverflowTooltip>{policyCostAfter !== null ? formatCurrency(policyCostAfter) : '-'}</OverflowTooltip>;
      },
    },
    {
      field: TransactionsFieldNames.Status,
      headerName: 'Status',
      flex: 1,
      sortable: false,
      renderCell: (params: TableGridRenderCellParams) => <OverflowTooltip>{params.row.status}</OverflowTooltip>,
    },
    {
      field: TransactionsFieldNames.Delete,
      headerName: '',
      flex: 0,
      sortable: false,
      align: 'right',
      renderCell: (params: TableGridRenderCellParams) => {
        const { status, transactionType, namedInsuredUpdateRequestId } = params.row;
        const showTrashcanIcon =
          !namedInsuredUpdateRequestId &&
          status !== PolicyTransactionStatus.RolledBack &&
          (status !== PolicyTransactionStatus.Processed ||
            transactionType === PolicyTransactionTypes.ConditionalRenewal);
        if (showTrashcanIcon) {
          return (
            <IconButton
              onClick={() => {
                handleOpenDialog(DialogType.Confirmation, params.row);
              }}
            >
              <TrashAlt />
            </IconButton>
          );
        }
        return null;
      },
    },
  ];
};

type PolicyApi = PolicyTransactionSearchResponse['items'][0];

const INITIAL_TRANSACTION_TYPES = ['NewBusiness', 'Rewrite'];

/**
 * Adds a `change` (premium delta vs. the prior Processed transaction) to each row.
 * Assumes `data` already arrives sorted by effectiveOn desc, then processedOn desc
 * (see the `sortConfig` passed to <Table> below) - this does not re-sort.
 */
export const buildTransactionHistoryRows = (data: PolicyApi[]): (PolicyApi & { change: number | null })[] => {
  if (!data) return [];

  const pastTransactions = data.filter(
    (t) => t.policyCostAfter !== null && t.status === PolicyTransactionStatus.Processed,
  );

  return data.map((el) => {
    const { id, policyCostAfter, transactionType } = el;
    const currentIndex = pastTransactions.findIndex((t) => t.id === id);
    const pastTransaction: PolicyTransaction | undefined =
      pastTransactions.length > currentIndex + 1 ? pastTransactions[currentIndex + 1] : undefined;

    const costAfterPreviousTransaction = pastTransaction?.policyCostAfter;
    const currentDifference =
      policyCostAfter !== null
        ? costAfterPreviousTransaction
          ? policyCostAfter - costAfterPreviousTransaction
          : -0
        : null;

    return {
      ...el,
      change: INITIAL_TRANSACTION_TYPES.includes(transactionType) ? policyCostAfter : currentDifference,
    };
  });
};

export const TransactionHistoryTab = () => {
  const { policyId } = useParams();
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);
  const { enqueueSnackbar } = useSnackbar();
  const { policy } = useGetPolicy({ policyId });

  const {
    policyTerms,
    isLoading: isPolicyTermsLoading,
    isError: isPolicyTermsError,
    refetch: refetchPolicyTerms,
  } = useSearchPolicyTerms({
    queryParams: { pageLimit: 100 },
    policyId: policyId ?? '',
  });

  const { policyTransactions: conditionalRenewalTransactions } = useSearchPolicyTransactions({
    policyId: policyId ?? '',
    transactionType: PolicyTransactionTypes.ConditionalRenewal,
    status: [PolicyTransactionStatus.Processed],
    includeCanceled: false,
    pageLimit: 20,
  });
  // The transaction table manages its own paginated query, but transaction
  // creation needs the full policy timeline to detect and confirm OoSE dates.
  const {
    policyTransactions,
    isLoading: isPolicyTransactionsLoading,
    isFetching: isPolicyTransactionsFetching,
    isError: isPolicyTransactionsError,
    refetch: refetchPolicyTransactions,
  } = useSearchPolicyTransactions({
    policyId: policyId ?? '',
    includeCanceled: false,
    pageLimit: 100,
  });

  const { updatePolicyTransaction, isPending: isPolicyTransactionLoading } = useUpdatePolicyTransaction();
  const { disassociateConditionalRenewalQuoteId } = useDisassociateConditionalRenewalQuoteId();
  const [selectedTransaction, setSelectedTransaction] = useState<undefined | PolicyTransaction>(undefined);
  const [deleteConditionalRenewalToo, setDeleteConditionalRenewalToo] = useState(false);
  const [dialog, setDialog] = useState<{
    show: boolean;
    type: AllPolicyDialogTypes | null;
  }>({
    show: false,
    type: null,
  });

  const selectedTransactionConditionalRenewal = useMemo(() => {
    if (!selectedTransaction || selectedTransaction.transactionType !== PolicyTransactionTypes.Renewal)
      return undefined;
    const quoteId = selectedTransaction.quote?.id;
    if (!quoteId) return undefined;
    return conditionalRenewalTransactions?.items?.find(
      (t) => t.policyConditionalRenewalTransaction?.renewalQuoteId === quoteId,
    );
  }, [selectedTransaction, conditionalRenewalTransactions]);

  const handleOpenDialog = useCallback((type: AllPolicyDialogTypes, transaction?: PolicyTransaction) => {
    setDialog({ type, show: true });
    if (transaction) {
      setSelectedTransaction(transaction);
    }
  }, []);

  // The API returns transactions pre-sorted by effectiveOn desc, then processedOn
  // desc (unprocessed first on ties) via the `sort` query param below, so no
  // client-side re-sort is needed here.
  const formatRows = useCallback(
    (data: PolicyApi[]) => buildTransactionHistoryRows(data),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isInternalUser, policy?.status],
  );

  const handleCloseDialog = () => {
    setDialog({ type: null, show: false });
    setSelectedTransaction(undefined);
    setDeleteConditionalRenewalToo(false);
  };

  const handleCancelTransaction = async () => {
    if (!selectedTransaction) return;
    const transactionBody: PolicyTransactionPatchRequest = {
      status: PolicyTransactionStatus.Canceled,
    };
    if (
      selectedTransaction.transactionType === PolicyTransactionTypes.Cancelation &&
      (selectedTransaction.policyCancelationTransaction?.source ||
        selectedTransaction.policyCancelationTransaction?.reason ||
        selectedTransaction.policyCancelationTransaction?.method ||
        selectedTransaction.policyCancelationTransaction?.replacementCarrier)
    ) {
      transactionBody.policyCancelationTransaction = {
        source: selectedTransaction.policyCancelationTransaction?.source,
        reason: selectedTransaction.policyCancelationTransaction?.reason,
        method: selectedTransaction.policyCancelationTransaction?.method,
        replacementCarrier: selectedTransaction.policyCancelationTransaction?.replacementCarrier,
      };
    } else if (
      selectedTransaction.transactionType === PolicyTransactionTypes.Reinstatement &&
      (selectedTransaction.policyReinstatementTransaction?.comments ||
        selectedTransaction.policyReinstatementTransaction?.reason ||
        selectedTransaction.policyReinstatementTransaction?.claimsLossesPostCancelation)
    ) {
      transactionBody.policyReinstatementTransaction = {
        comments: selectedTransaction.policyReinstatementTransaction?.comments,
        reason: selectedTransaction.policyReinstatementTransaction?.reason,
        claimsLossesPostCancelation: selectedTransaction.policyReinstatementTransaction?.claimsLossesPostCancelation,
        certifiedMailRequired: selectedTransaction.policyReinstatementTransaction?.certifiedMailRequired ?? undefined,
      };
    }

    // Capture before async: selectedTransaction/state will be cleared in finally
    const isRenewalWithCR =
      selectedTransaction.transactionType === PolicyTransactionTypes.Renewal && !!selectedTransactionConditionalRenewal;
    const shouldDeleteCR = deleteConditionalRenewalToo;
    const conditionalRenewalId = selectedTransactionConditionalRenewal?.id;

    await updatePolicyTransaction(
      {
        policyTransactionId: selectedTransaction.id,
        body: transactionBody,
      },
      {
        onSuccess: async () => {
          if (selectedTransaction.transactionType === PolicyTransactionTypes.Renewal) {
            enqueueSnackbar(`Renewal Cancelled`, {
              variant: ToastType.Success,
            });
          }
          if (selectedTransaction.transactionType === PolicyTransactionTypes.Endorsement) {
            enqueueSnackbar(`Endorsement Cancelled`, {
              variant: ToastType.Success,
            });
          } else {
            enqueueSnackbar(`Transaction deleted`, {
              variant: ToastType.Success,
            });
          }

          if (isRenewalWithCR && conditionalRenewalId) {
            if (shouldDeleteCR) {
              try {
                await updatePolicyTransaction(
                  { policyTransactionId: conditionalRenewalId, body: { status: PolicyTransactionStatus.Canceled } },
                  {
                    onError: async (e) => {
                      const message = await getApiErrorMessage(e, 'Failed to delete Conditional Renewal');
                      enqueueSnackbar(message, {
                        variant: ToastType.Error,
                        persist: true,
                      });
                    },
                  },
                );
              } catch {
                // Error is already surfaced via onError; do not let this side-effect fail the main action.
              }
              return;
            }
            // don't delete, disassociate the CR from the renewal
            await disassociateConditionalRenewalQuoteId(conditionalRenewalId).catch(async (e) => {
              const message = await getApiErrorMessage(e, 'Failed to disassociate Conditional Renewal');
              enqueueSnackbar(message, {
                variant: ToastType.Error,
                persist: true,
              });
            });
          }
        },
        onError: () => {
          if (selectedTransaction.transactionType === PolicyTransactionTypes.Renewal) {
            enqueueSnackbar(`Cancellation of Renewal Failed`, {
              variant: ToastType.Error,
              persist: true,
            });
          } else {
            enqueueSnackbar(`Deletion of ${selectedTransaction.transactionType} failed`, {
              variant: ToastType.Error,
              persist: true,
            });
          }
        },
      },
    ).finally(() => {
      setSelectedTransaction(undefined);
      setDeleteConditionalRenewalToo(false);
    });
  };

  const showPolicyTransactionDialog = [
    PolicyDialogType.Renew,
    PolicyDialogType.Rewrite,
    PolicyDialogType.Reinstatement,
    EndorsementPolicyDialogType.Endorsement,
    PolicyDialogType.Cancelation,
    PolicyDialogType.Rescindment,
    RenewDialogType.ConditionalRenewalView,
    RenewDialogType.DoNotRenewView,
  ].includes(dialog.type as any);
  const requiresPolicyTimeline = dialog.type === EndorsementPolicyDialogType.Endorsement;

  const policyDetailsTransactionHistoryColumns = getPolicyDetailsTransactionHistoryColumns({
    isInternalUser,
    handleOpenDialog,
  });

  const confirmationDescriptionText = `Are you sure you want ${
    selectedTransaction?.transactionType === PolicyTransactionTypes.Endorsement
      ? 'to cancel this endorsement?'
      : selectedTransaction?.transactionType === PolicyTransactionTypes.Renewal
        ? 'to cancel this renewal?'
        : 'to delete this transaction?'
  }`;
  const isRenewalWithCR =
    selectedTransaction?.transactionType === PolicyTransactionTypes.Renewal && !!selectedTransactionConditionalRenewal;

  return (
    <>
      <Table<{
        items: PolicyApi[];
        params: PolicyTransactionSearchRequestQuery;
      }>
        ariaLabel='transaction-history-tab-grid'
        columns={policyDetailsTransactionHistoryColumns}
        onLoadData={useSearchPolicyTransactions}
        rowFormatter={formatRows}
        noResultsConfig={{ noResultsMessage: 'No Transactions found' }}
        isInitiating={isPolicyTermsLoading}
        // The Change column is computed from `data`, which is only the current page
        // (Table defaults to 10 rows/page). A larger page size makes it far less likely
        // a policy's transaction count crosses a page boundary and breaks that calc -
        // it isn't a complete fix, just a safe margin for the vast majority of policies.
        paginationConfig={{ currentPage: 0, pageOffset: 0, rowsPerPage: 50 }}
        filterConfig={{
          defaultFilters: {
            policyId,
            includeCanceled: 'false',
          },
          filterQueryParam: {
            policyId: 'policyId',
            includeCanceled: 'includeCanceled',
          },
        }}
        sortConfig={{
          // All columns are sortable: false, so this default can't be overridden from the UI -
          // it just tells the API to order by effectiveOn desc, then processedOn desc
          // (unprocessed transactions sort first on effectiveOn ties).
          sortQueryParam: {
            [TransactionsFieldNames.EffectiveDate]: (order) => `${order}effectiveOn,${order}processedOn`,
          },
          defaultSort: { field: TransactionsFieldNames.EffectiveDate, sort: 'desc' },
        }}
        sxProps={{
          tableContainer: {
            ...styles.dataGridContainer,
            display: 'block',
            height: 'fit-content',
          },
          dataGridContainer: {
            ...styles.dataGridContainer,
            borderLeft: 'none',
            padding: 0,
            height: 'auto',
          },
          dataGrid: styles.dataGrid,
        }}
      />
      {dialog.type && showPolicyTransactionDialog && !!policy ? (
        <CreatePolicyTransactionDialog
          isOpen={dialog.show ?? false}
          policyTerms={policyTerms?.items ?? []}
          policyTermsError={isPolicyTermsError}
          onRetryPolicyTerms={() => void refetchPolicyTerms()}
          policy={policy}
          onCancel={handleCloseDialog}
          selectedPolicyTransaction={selectedTransaction}
          policyTransactions={policyTransactions?.items ?? []}
          isDialogLoading={requiresPolicyTimeline && (isPolicyTransactionsLoading || isPolicyTransactionsFetching)}
          policyTransactionsError={requiresPolicyTimeline && isPolicyTransactionsError}
          onRetryPolicyTransactions={() => void refetchPolicyTransactions()}
          dialogType={dialog.type as any}
        />
      ) : null}

      {policy && dialog.type === EndorsementPolicyDialogType.UpdateBillPlan ? (
        <UpdateBillPlanDialog
          policyTransactions={policyTransactions?.items ?? []}
          policyTerms={[]}
          handleHide={handleCloseDialog}
          policy={policy}
          isOpen={dialog.show}
          selectedPolicyTransaction={selectedTransaction}
        />
      ) : null}
      {dialog.type === DialogType.Confirmation && (
        <ConfirmationDialog
          onCancel={handleCloseDialog}
          isOpen={dialog.show && !!selectedTransaction}
          isLoading={isPolicyTransactionLoading}
          description={isRenewalWithCR ? undefined : confirmationDescriptionText}
          buttonConfig={{
            confirmText:
              selectedTransaction?.transactionType === PolicyTransactionTypes.Endorsement ? 'Yes, cancel' : 'Yes',
            secondaryText: selectedTransaction?.transactionType === PolicyTransactionTypes.Endorsement ? 'Exit' : 'No',
            onConfirm: handleCancelTransaction,
          }}
        >
          {isRenewalWithCR && (
            <>
              <Typography>{confirmationDescriptionText}</Typography>
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Checkbox
                  checked={deleteConditionalRenewalToo}
                  onChange={(e) => setDeleteConditionalRenewalToo(e.target.checked)}
                  sx={{ pl: 0 }}
                />
                <Typography variant='bodySmall'>Delete the Conditional Renewal as well</Typography>
              </Box>
            </>
          )}
        </ConfirmationDialog>
      )}

      {dialog.type === PolicyDialogType.BrokerReassignment && selectedTransaction && policy && (
        <BrokerReassignedDialog
          policy={policy as PolicyGetResponse}
          handleCloseDialog={handleCloseDialog}
          selectedTransaction={selectedTransaction}
        />
      )}
    </>
  );
};
